import Joi from 'joi'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { addDays, subDays } from 'date-fns'
import { endOfDayVienna, formatVienna, startOfDayVienna, toDate } from '../../../../shared/datetime'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../UI/Btn'
import TextField from '../../../../UI/TextField'
import SelectField from '../../../../UI/SelectField'
import TextAreaField from '../../../../UI/TextAreaField'
import DateTimeField from '../../../../UI/DateTimeField'
import BaseModal from '../../../../UI/BaseModal/BaseModal'
import RecordComments from '../../../../UI/RecordComments'
import CoverUploader from '../../../../UI/CoverUploader'

import http from '../../../../shared/http'
import { useAvatarUrl } from '../../../../shared/useAvatarUrls'
import { schemaOptions } from '../../../../shared/i18n'
import { types } from '../../../../shared/eventEmitter'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { stateLabel } from '../../../../shared/helpers'
import { TApi, TCompanies, TRoles, StateOptions, TNews, TLanguages, RECORD_TYPES } from '../../../../shared/const'

import styles from './style.module.scss'
import injectRole from '../../../HOC/injectRole'

/*
 * Nachricht anlegen und bearbeiten.
 *
 * Das Formular hat siebzehn Felder. Untereinander in einem schmalen Dialog war
 * das eine Scrollstrecke, bei der man den Ueberblick verliert — die
 * Pflichtfelder standen zwischen den optionalen, und wie weit man ist, sah man
 * nur an der Bildlaufleiste. Deshalb Register, je eines pro Frage:
 *
 *   Inhalt          was steht drin
 *   Adresse         wo — Anschrift, Link oder beides
 *   Zielgruppen     wer sieht sie
 *   Datum & Zeit    wann ist sie sichtbar (und wann findet es statt)
 *   Pruefen & Senden alles auf einem Schirm, bevor gespeichert wird
 *   Verlauf         wer hat wann was geaendert
 *
 * Die ersten fuenf sind ein Weg mit Nummern; der Verlauf ist ein
 * Nachschlagewerk und steht abgesetzt — bei einer neuen Nachricht gibt es ihn
 * gar nicht.
 *
 * Der Zeitraum steht bewusst spaet: Er ist die letzte Entscheidung, nachdem
 * feststeht, was drinsteht und wer es bekommt — und nicht die erste Huerde vor
 * dem Inhalt.
 *
 * Geprueft wird ueber alle Register: Beim Speichern springt der Dialog auf das
 * erste Register mit Fehlern, und jedes betroffene Register bekommt einen Punkt.
 *
 * Beim Bearbeiten steht "Speichern" auf jedem Register — wer nur den Endtermin
 * aendert, soll nicht erst durch alle Schritte blaettern muessen. Beim Anlegen
 * fuehrt der nummerierte Weg zum Schluss-Register, weil dort zum ersten Mal
 * alles zusammen steht.
 */

const gemeinsameFelder = {
	newsType: Joi.any().valid(TNews.EVENT, TNews.NEWS).required(),
	title: Joi.string().max(255).required(),
	description: Joi.string().max(500).required(),
	place: Joi.string().max(255).empty('').allow(null).default(null),
	street: Joi.string().max(255).empty('').allow(null).default(null),
	zip: Joi.string().max(32).empty('').allow(null).default(null),
	city: Joi.string().max(255).empty('').allow(null).default(null),
	country: Joi.string().max(255).empty('').allow(null).default(null),
	externalLink: Joi.string().uri().empty('').allow(null).default(null),
	states: Joi.array().min(1).items(Joi.string()).allow(null).allow('').default(null),
	roles: Joi.array().min(1).items(Joi.string()).allow(null).default(null),
	schoolType: Joi.array().min(1).items(Joi.string()).allow(null).default(null),
	companyTypes: Joi.array().min(1).items(Joi.string()).allow(null).default(null),
	companyBranch: Joi.array().min(1).items(Joi.string()).allow(null).default(null),
	sendEmail: Joi.boolean().default(false),
	/*
	 * Entwurf oder aktiv (Anforderung #164) — die Entscheidung faellt im
	 * Register "Pruefen & Senden". Beim Anlegen ist sie Pflicht und nicht
	 * vorbelegt: Wer speichert, hat sich entschieden. Beim Bearbeiten und in
	 * der Kopie steht der Stand der Nachricht bzw. der Vorlage drin.
	 */
	publishStatus: Joi.string().valid('ACTIVE', 'DRAFT').empty('').required()
}

const endeNachStart = Joi.date().greater(Joi.ref('startDate')).empty('').allow(null).default(null).required()

/*
 * Der Veranstaltungstermin ist etwas anderes als der Anzeigezeitraum: Eine
 * Messe wird monatelang angekuendigt und dauert einen Tag. Er gilt deshalb nur
 * bei einer Veranstaltung — eine Neuigkeit hat keinen Termin, sondern nur die
 * Zeitspanne, in der sie zu sehen ist.
 *
 * Das Ende bleibt freiwillig: Die meisten Veranstaltungen sind ein Zeitpunkt.
 */
const terminFelder = {
	eventStart: Joi.date()
		.empty('')
		.allow(null)
		.default(null)
		.when('newsType', { is: TNews.EVENT, then: Joi.required().invalid(null) }),
	eventEnd: Joi.date().greater(Joi.ref('eventStart')).empty('').allow(null).default(null)
}

/*
 * Zwei Schemata, weil "in der Vergangenheit" beim Anlegen und beim Bearbeiten
 * Verschiedenes bedeutet.
 *
 * Beim Anlegen ist ein Start von gestern fast immer ein Vertipper — die
 * Mitteilung waere schon abgelaufen, bevor sie jemand sieht. Beim Bearbeiten
 * dagegen ist die Vergangenheit der Normalfall: Von 25 Nachrichten sind 17
 * abgelaufen. Dieselbe Regel machte jede davon unspeicherbar — wer die Adresse
 * nachtragen wollte, bekam einen Fehler am Startdatum, das er gar nicht
 * angefasst hatte. Ein rueckwirkend verschobener Zeitraum ist eine bewusste
 * Entscheidung der Administration, keine Fehleingabe.
 *
 * "Ende nach Start" gilt in beiden Faellen — das ist keine Frage des Zeitpunkts,
 * sondern der Logik.
 */
const schemaAnlegen = Joi.object({
	...gemeinsameFelder,
	...terminFelder,
	startDate: Joi.date().min(subDays(new Date(), 1)).empty('').allow(null).default(null).required(),
	endDate: endeNachStart.min(subDays(new Date(), 1))
})

/*
 * Beim Bearbeiten ist auch der Veranstaltungstermin keine Pflicht. Nachgetragen
 * wurde er aus dem bisherigen Zeitraum; wo der leer war, ist er es auch, und
 * eine Pflichtangabe machte genau diese Altbestaende unspeicherbar.
 */
const schemaBearbeiten = Joi.object({
	...gemeinsameFelder,
	...terminFelder,
	eventStart: Joi.date().empty('').allow(null).default(null),
	startDate: Joi.date().empty('').allow(null).default(null).required(),
	endDate: endeNachStart
})

const TAB_SCHEDULE = 'schedule'
const TAB_CONTENT = 'content'
const TAB_ADDRESS = 'address'
const TAB_AUDIENCE = 'audience'
const TAB_REVIEW = 'review'
const TAB_HISTORY = 'history'

/*
 * Jedes Feld gehoert zu genau einem Register — daran haengen der Fehlerpunkt am
 * Reiter und der Sprung beim Speichern. Ein Feld in zwei Registern liesse den
 * Sprung ins falsche laufen.
 *
 * Der Verlauf ist kein Schritt, sondern ein Nachschlagewerk: keine Nummer, und
 * bei einer neuen Nachricht gibt es ihn gar nicht.
 */
const TABS = [
	{
		key: TAB_CONTENT,
		label: 'Content',
		fields: ['newsType', 'title', 'description']
	},
	{
		key: TAB_ADDRESS,
		label: 'Address',
		fields: ['street', 'zip', 'city', 'country', 'place', 'externalLink']
	},
	{
		key: TAB_AUDIENCE,
		label: 'Target groups',
		fields: ['states', 'roles', 'schoolType', 'companyTypes', 'companyBranch']
	},
	{
		key: TAB_SCHEDULE,
		label: 'Date and time',
		fields: ['startDate', 'endDate', 'eventStart', 'eventEnd']
	},
	{ key: TAB_REVIEW, label: 'Check and send', fields: ['publishStatus'] },
	{ key: TAB_HISTORY, label: 'Comments', fields: [], onlyExisting: true, noStep: true }
]

// Das erste Register ergibt sich aus der Reihenfolge oben — so bleibt der
// Einstieg richtig, wenn die Schritte einmal anders sortiert werden.
const ERSTES_REGISTER = TABS[0].key

// Welches Register ein Feld beherbergt — fuer den Verweis "Aendern" in der
// Zusammenfassung.
const registerFuerFeld = feld => TABS.find(item => item.fields.includes(feld))?.key || ERSTES_REGISTER

// Datum und Uhrzeit in einem Feld — die Uhrzeit ist Teil des Zeitpunkts, zwei
// Felder waeren zwei Stellen, an denen dasselbe schiefgehen kann.
const DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm'

/*
 * Vorbelegung, wenn nichts dasteht: ab heute, einen Monat lang sichtbar.
 *
 * Ganze Tage statt einer Uhrzeit aus dem Moment des Oeffnens — bei einer
 * Mitteilung geht es um Sichtbarkeit, nicht um einen Termin auf die Minute.
 * Wer eine Veranstaltung erfasst, traegt die echte Uhrzeit ohnehin ein; die
 * Vorbelegung soll nur verhindern, dass zwei Pflichtfelder leer dastehen.
 */
const VORBELEGUNG_TAGE = 30
// Tagesgrenzen in Wiener Zeit: "ab heute" heisst 00:00 in Oesterreich, nicht
// 00:00 auf dem Rechner, an dem die Nachricht gerade erfasst wird.
const standardStart = () => startOfDayVienna(new Date())
const standardEnde = von => endOfDayVienna(addDays(von || new Date(), VORBELEGUNG_TAGE))

const zuDatum = toDate

// Zusammenfassung und Verlauf nennen die Uhrzeit mit — sie ist jetzt Teil der
// Eingabe, und eine Zeitangabe ohne sie waere nur die halbe Auskunft.
const fmtDateTime = value => formatVienna(value, DATE_TIME_FORMAT) || null

/*
 * Eine Zeile der Zusammenfassung. Sie fuehrt zurueck zu dem Register, in dem der
 * Wert erfasst wird: Wer beim Pruefen etwas entdeckt, will es dort aendern und
 * nicht erst suchen, in welchem Schritt es stand.
 */
function SummaryRow({ label, onEdit, editLabel, children }) {
	return (
		<div className={styles.summaryRow}>
			<span className={styles.summaryLabel}>{label}</span>
			<span className={styles.summaryValue}>{children}</span>
			{onEdit && (
				<button type="button" className={styles.summaryEdit} onClick={onEdit}>
					{editLabel}
				</button>
			)}
		</div>
	)
}

SummaryRow.propTypes = {
	label: PropTypes.string.isRequired,
	onEdit: PropTypes.func,
	editLabel: PropTypes.string,
	children: PropTypes.node
}

function AddFeedModal(props) {
	const { t, i18n } = useTranslation()
	/*
	 * Anlegen oder Bearbeiten — als Referenz, nicht als Zustand: Der Pruefer
	 * wird von react-hook-form aufgerufen, das beim ersten Rendern eine feste
	 * Fassung merkt. Eine Zustandsvariable waere darin veraltet, die Referenz
	 * ist immer die aktuelle.
	 */
	const istBearbeitung = useRef(false)
	const {
		reset,
		watch,
		control,
		setValue,
		trigger,
		register,
		handleSubmit,
		formState: { errors, dirtyFields }
	} = useForm({
		/*
		 * Den Fehlerfokus uebernimmt react-hook-form nicht: Es versucht, das
		 * erste fehlerhafte Feld anzuspringen, und ruft dazu focus() auf dessen
		 * Referenz. Bei den Datumsfeldern ist das eine react-datepicker-Instanz
		 * und kein DOM-Element — der Aufruf endet in "elm.focus is not a
		 * function" und reisst die Ansicht ab. Auf das richtige Register springt
		 * der Dialog ohnehin selbst (onInvalid).
		 */
		shouldFocusError: false,
		resolver: (werte, kontext, optionen) =>
			joiResolver(istBearbeitung.current ? schemaBearbeiten : schemaAnlegen, {
				errors: {
					labels: false,
					language: localStorage.getItem('i18nextLng')
				},
				...schemaOptions
			})(werte, kontext, optionen)
	})

	const [id, setId] = useState(null)
	/*
	 * Eine Kopie ist eine neue Nachricht mit den Werten einer bestehenden.
	 * Sie braucht einen eigenen Merker, weil sie sich an "id" nicht ablesen
	 * laesst: Die ist null wie beim Anlegen, das Fenster soll aber sagen,
	 * woher die Werte kommen, und der Termin der Vorlage soll erklaert werden,
	 * wo er nicht uebernommen wurde.
	 */
	const [istKopie, setIstKopie] = useState(false)
	// Nur fuer den Hinweis: Der Termin der Vorlage lag in der Vergangenheit und
	// steht deshalb nicht in der Kopie.
	const [terminVerworfen, setTerminVerworfen] = useState(false)
	const [tab, setTab] = useState(ERSTES_REGISTER)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)
	const [isLoading, setLoading] = useState(false)
	const [onDone, setOnDone] = useState(null)
	// BaseModal haelt den Offen-Zustand fuer sich, der Rumpf dieser Komponente
	// laeuft aber auch bei geschlossenem Dialog. Ohne eigenen Merker fragte die
	// Empfaengerzahl im Hintergrund ab, ohne dass jemand den Dialog offen hat.
	const [isOpen, setIsOpen] = useState(false)
	const [recipients, setRecipients] = useState(null)
	// Ohne eigenen Merker sind "wird gerade geholt" und "geht nicht" dasselbe:
	// beides waere ein Datensatz weniger und damit dauerhaft "…". Bei einer
	// Zahl, die gross und prominent steht, ist das die falsche Auskunft.
	const [reachFailed, setReachFailed] = useState(false)
	// null = noch nicht geladen, [] = geladen und leer.
	const [history, setHistory] = useState(null)
	const [historyFailed, setHistoryFailed] = useState(false)

	/*
	 * Das Titelbild laeuft neben dem Formular her, nicht darin: Es geht als
	 * Datei zum Server, waehrend der Rest JSON ist, und beim Anlegen gibt es die
	 * Nachricht, zu der es gehoert, noch gar nicht. Deshalb drei eigene Merker
	 * statt eines Feldes im Formular.
	 *
	 *   bildName      was gespeichert ist (Dateiname aus der Nachricht)
	 *   bildDatei     was gerade gewaehlt und zugeschnitten wurde
	 *   bildEntfernen das gespeicherte Bild soll beim Speichern weg
	 */
	const [bildName, setBildName] = useState(null)
	const [bildDatei, setBildDatei] = useState(null)
	const [bildEntfernen, setBildEntfernen] = useState(false)

	const values = watch()

	/*
	 * Vorschau des Titelbildes. Das gespeicherte Bild wird angemeldet geholt
	 * (die Bild-Route antwortet nur Angemeldeten); ein eben gewaehltes liegt
	 * dagegen im Browser und bekommt eine lokale Adresse. Die muss wieder
	 * freigegeben werden — sonst behaelt der Browser jedes einmal gewaehlte Bild
	 * bis zum Neuladen der Seite.
	 */
	const gespeichertesBild = useAvatarUrl(bildEntfernen ? null : bildName)
	const [bildVorschau, setBildVorschau] = useState('')

	useEffect(() => {
		if (!bildDatei) return undefined

		const url = URL.createObjectURL(bildDatei)
		setBildVorschau(url)

		return () => {
			URL.revokeObjectURL(url)
			setBildVorschau('')
		}
	}, [bildDatei])

	const bildAnzeige = bildDatei ? bildVorschau : gespeichertesBild

	const rolesOptions = [TRoles.STUDENT, TRoles.TEACHER].map(item => ({
		label: t(item),
		value: item
	}))
	const companyTypesOptions = Object.values(TCompanies).map(item => ({
		label: item,
		value: item
	}))
	const newsTypesOptions = Object.values(TNews).map(item => ({
		label: t(item),
		value: item
	}))

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	/*
	 * Wie viele Personen die Nachricht erreicht. Der Server rechnet das nach
	 * denselben Regeln, nach denen die Lesesichten spaeter filtern.
	 *
	 * Die Zielgruppen haengen als JSON in den Abhaengigkeiten: Ein Array ist
	 * bei jedem Rendern eine neue Referenz und wuerde die Abfrage endlos
	 * ausloesen. Dazu eine kurze Wartezeit, damit das Klicken durch eine
	 * Mehrfachauswahl nicht jede Zwischenstufe abfragt.
	 */
	const zielgruppenSchluessel = JSON.stringify({
		states: values.states || null,
		roles: values.roles || null,
		schoolType: values.schoolType || null,
		companyTypes: values.companyTypes || null,
		companyBranch: values.companyBranch || null
	})

	useEffect(() => {
		if (!isOpen) return undefined

		const timer = setTimeout(() => {
			http.post(TApi.ADMIN_NEWS_RECIPIENTS, JSON.parse(zielgruppenSchluessel), {
				// Die Zahl ist eine Hilfe, kein Arbeitsschritt. Scheitert sie,
				// soll kein Fehlerbalken ueber dem Formular liegen — der Hinweis
				// steht stattdessen an der Stelle der Zahl.
				disableToast: true
			})
				.then(({ data }) => {
					setRecipients(data)
					setReachFailed(false)
				})
				.catch(() => {
					setRecipients(null)
					setReachFailed(true)
				})
		}, 400)

		return () => clearTimeout(timer)
	}, [zielgruppenSchluessel, isOpen])

	/*
	 * Der Verlauf wird erst geholt, wenn jemand ihn aufschlaegt — nicht beim
	 * Oeffnen des Dialogs. Er ist ein Nachschlagewerk; die meisten Bearbeitungen
	 * kommen ohne ihn aus, und dann muss auch nichts geladen werden.
	 */
	useEffect(() => {
		if (tab !== TAB_HISTORY || !id || history !== null) return

		http.get(TApi.ADMIN_NEWS_CHANGES(id), { disableToast: true })
			.then(({ data }) => {
				setHistory(data)
				setHistoryFailed(false)
			})
			.catch(() => setHistoryFailed(true))
		/* eslint-disable-next-line */
	}, [tab, id, history])

	const branchLabel = value => {
		const option = props.branch?.find(v => v.value_EN === value)
		if (!option) return value

		return i18n.language === TLanguages.EN ? option.value_EN : option.value_DE
	}

	/*
	 * Der Server legt im Verlauf die rohen Feldnamen und Werte ab — er weiss
	 * nichts von Beschriftungen und kennt die Sprache des Lesers nicht. Benannt
	 * und uebersetzt wird deshalb hier, und zwar mit denselben Beschriftungen wie
	 * im Formular: Ein Verlauf, der andere Woerter benutzt als die Felder daneben,
	 * zwingt zum Uebersetzen im Kopf.
	 */
	const FELD_LABELS = {
		newsType: t('Message type'),
		isDraft: t('Status'),
		pinnedAt: t('Pinned to top'),
		title: t('Title'),
		description: t('description'),
		imageHashName: t('Cover image'),
		startDate: t('Shown from'),
		endDate: t('Shown until'),
		eventStart: t('Event start'),
		eventEnd: t('Event end'),
		street: t('Street and number'),
		zip: t('Postal code'),
		city: t('City or town'),
		country: t('Country'),
		place: t('Location (previous entry)'),
		externalLink: t('External Link'),
		states: t('state'),
		roles: t('User roles'),
		schoolType: t('School Types'),
		companyTypes: t('Company type'),
		companyBranch: t('branch')
	}

	const feldLabel = feld => FELD_LABELS[feld] || feld

	const feldWert = (feld, wert) => {
		/*
		 * Beim Anheften legt der Server den Zeitpunkt ab, leer heisst "nicht
		 * angeheftet". Im Verlauf zaehlt nur das Ob — und "leer" waere hier
		 * keine Auskunft, deshalb steht die Pruefung vor der allgemeinen.
		 */
		if (feld === 'pinnedAt') return wert ? t('Pinned') : t('Not pinned')

		if (wert === null || wert === undefined || (Array.isArray(wert) && !wert.length)) return t('empty')

		if (['startDate', 'endDate', 'eventStart', 'eventEnd'].includes(feld)) return fmtDateTime(wert) || String(wert)
		if (feld === 'newsType') return t(wert)
		// Der Server legt Booleans als 'true'/'false' ab; im Verlauf sollen die
		// Woerter des Statusfelds stehen.
		if (feld === 'isDraft') return String(wert) === 'true' ? t('Draft') : t('Active')
		/*
		 * Beim Titelbild legt der Server nicht den Dateinamen in den Verlauf,
		 * sondern nur, dass eines dastand: Ein Name wie "3f7c….jpg" waere fuer
		 * die Lesenden des Verlaufs keine Auskunft.
		 */
		if (feld === 'imageHashName') return t('Image set')

		if (Array.isArray(wert)) {
			const label = {
				states: stateLabel,
				roles: rolle => t(rolle),
				companyBranch: branchLabel
			}[feld]

			return wert.map(eintrag => (label ? label(eintrag) : eintrag)).join(' · ')
		}

		return String(wert)
	}

	// Nur eine Veranstaltung hat einen Termin. Eine Neuigkeit hat den Zeitraum,
	// in dem sie zu sehen ist — und sonst nichts.
	const istVeranstaltung = values.newsType === TNews.EVENT

	/*
	 * Der Termin darf ausserhalb des Anzeigezeitraums liegen — eine Ankuendigung
	 * darf vorher auslaufen, und eine Nachbetrachtung darf danach beginnen. Fast
	 * immer ist es aber ein Versehen, deshalb ein Hinweis und kein Fehler.
	 */
	const terminAusserhalb = (() => {
		if (!istVeranstaltung) return null

		const beginn = zuDatum(values.eventStart)
		const anzeigeVon = zuDatum(values.startDate)
		const anzeigeBis = zuDatum(values.endDate)
		if (!beginn) return null

		if (anzeigeBis && beginn > anzeigeBis) return t('The display period ends before the event takes place.')
		if (anzeigeVon && beginn < anzeigeVon) return t('The event takes place before the message is shown.')

		return null
	})()

	// Leer oder "ALL" heisst: keine Einschraenkung.
	const audienceValue = (list, label = value => value) => {
		if (!list?.length || list.includes('ALL')) return null

		return list.map(label).join(', ')
	}

	const audience = [
		{ key: 'roles', label: t('User roles'), value: audienceValue(values.roles, role => t(role)) },
		{ key: 'states', label: t('state'), value: audienceValue(values.states, stateLabel) },
		{ key: 'schoolType', label: t('School Types'), value: audienceValue(values.schoolType) },
		{ key: 'companyTypes', label: t('Company type'), value: audienceValue(values.companyTypes) },
		{ key: 'companyBranch', label: t('branch'), value: audienceValue(values.companyBranch, branchLabel) }
	]

	const restrictions = audience.filter(item => item.value)

	// Anschrift als eine Zeile fuer die Zusammenfassung: "Strasse, PLZ Ort, Land",
	// aber nur mit den Teilen, die auch dastehen.
	const anschrift = [values.street, [values.zip, values.city].filter(Boolean).join(' '), values.country]
		.map(teil => (teil || '').trim())
		.filter(Boolean)
		.join(', ')

	/*
	 * Das Titelbild, nachdem die Nachricht gespeichert ist.
	 *
	 * Es geht als Datei zu einer eigenen Adresse — im Formular steht JSON, und
	 * beim Anlegen gibt es die Kennung, zu der das Bild gehoert, erst nach dem
	 * Speichern.
	 *
	 * Die Kopie nimmt das Bild der Vorlage mit: Derselbe Aufruf fuer ein
	 * weiteres Bundesland ist der haeufige Fall, und dort gehoert dasselbe Bild
	 * dazu. Geholt wird es dafuer noch einmal und als neue Datei abgelegt —
	 * sonst haetten zwei Nachrichten dieselbe Datei, und das Loeschen der einen
	 * naehme der anderen das Bild weg.
	 *
	 * Fehler hier reissen das Speichern nicht mit: Die Nachricht steht bereits.
	 * Gesagt wird es trotzdem, sonst glaubt die Redaktion, das Bild sei dabei.
	 */
	const bildUebertragen = async newsId => {
		const hochladen = async datei => {
			const daten = new FormData()
			daten.append('image', datei)

			await http.put(TApi.ADMIN_NEWS_IMAGE(newsId), daten, { disableToast: true })
		}

		if (bildDatei) return hochladen(bildDatei)
		if (bildEntfernen && id) return http.delete(TApi.ADMIN_NEWS_IMAGE(id), { disableToast: true })

		// Kopie ohne eigene Auswahl: das Bild der Vorlage uebernehmen.
		if (istKopie && bildName && !bildEntfernen) {
			const { data } = await http.get(TApi.PROFILE_AVATAR(bildName), {
				responseType: 'blob',
				disableToast: true
			})

			return hochladen(new File([data], 'titelbild.jpg', { type: data.type || 'image/jpeg' }))
		}

		return null
	}

	const onSubmit = (fields, closeModal) => {
		setLoading(true)
		if (fields.states?.some(state => state === 'ALL')) fields.states = null
		// sendEmail hat kein Bedienelement mehr (siehe Register "Pruefen &
		// Senden"), im Schema des Servers ist es aber Pflicht. Ausdruecklich
		// setzen statt auf den Vorgabewert des Prueflaufs zu bauen — fehlt das
		// Feld, weist der Server die ganze Anfrage ab.
		const form = { ...fields, sendEmail: false, isDraft: fields.publishStatus === 'DRAFT' }
		// Der Server kennt nur isDraft; ein unbekanntes Feld wiese er ab.
		delete form.publishStatus

		const request = id ? http.put(TApi.ADMIN_PUT_NEWS(id), form) : http.post(TApi.ADMIN_POST_NEWS, form)

		request
			.then(({ data }) =>
				bildUebertragen(id || data?.id)
					.catch(() => toast.warn(t('The message was saved, but the cover image was not transferred.')))
					.then(() => {
						toast.success(t(id ? 'The message was updated' : 'The message was created'))
						closeModal()
						if (onDone) onDone()
					})
			)
			.finally(() => {
				setLoading(false)
			})
	}

	// Beim Speichern auf das erste Register mit Fehlern springen — sonst meldet
	// der Dialog einen Fehler, den man auf dem offenen Register nicht sieht.
	const onInvalid = formErrors => {
		const target = TABS.find(item => item.fields.some(field => formErrors[field]))
		if (target) setTab(target.key)
	}

	/*
	 * Oeffnet zum Anlegen (ohne Vorlage), zum Bearbeiten oder als Kopie.
	 *
	 * Die Kopie ist die dritte Art, dasselbe Fenster zu benutzen: Sie bekommt
	 * alle Werte der Vorlage, aber keine Kennung — gespeichert wird sie als
	 * neue Nachricht. Deshalb gilt fuer sie auch das Schema des Anlegens: Ein
	 * Anzeigebeginn in der Vergangenheit ist bei einer neuen Nachricht ein
	 * Vertipper, auch wenn er aus einer alten stammt.
	 */
	const handleOpen = (feed = null, done = null, optionen = {}) => {
		const kopie = Boolean(optionen.kopie && feed)

		setOnDone(() => done)
		setTab(ERSTES_REGISTER)
		setId(kopie ? null : feed?.id || null)
		setIstKopie(kopie)
		istBearbeitung.current = !kopie && !!feed?.id
		setIsOpen(true)
		setRecipients(null)
		setHistory(null)
		setHistoryFailed(false)

		/*
		 * Das Bild der Vorlage bleibt auch in der Kopie stehen — anders als der
		 * Termin, der zum einzelnen Ereignis gehoert. Uebertragen wird es beim
		 * Speichern (siehe bildUebertragen).
		 */
		setBildName(feed?.imageHashName || null)
		setBildDatei(null)
		setBildEntfernen(false)

		/*
		 * Vorbelegung: Ein fehlender Zeitraum wird nicht als leeres Pflichtfeld
		 * hingelegt, sondern mit dem Normalfall gefuellt — ab heute, einen Monat
		 * lang. Das gilt beim Anlegen wie beim Bearbeiten einer Nachricht, der
		 * eine der beiden Angaben fehlt.
		 *
		 * Die Kopie faengt immer damit an: Der Zeitraum der Vorlage ist bei fast
		 * jeder Kopie schon gelaufen — uebernommen waere er entweder abgelaufen
		 * oder gar nicht speicherbar.
		 */
		const start = (!kopie && zuDatum(feed?.startDate)) || standardStart()
		setValue('startDate', start)
		setValue('endDate', (!kopie && zuDatum(feed?.endDate)) || standardEnde(start))

		/*
		 * Der Termin wird NICHT vorbelegt. Der Anzeigezeitraum hat einen
		 * sinnvollen Normalfall (ab heute, einen Monat lang); ein Termin hat
		 * keinen — ein geratenes Datum, das man uebersieht, steht danach als
		 * Ankuendigung in der Welt.
		 *
		 * Aus derselben Ueberlegung nimmt die Kopie den Termin der Vorlage nur
		 * mit, solange er noch bevorsteht: Dieselbe Veranstaltung fuer ein
		 * weiteres Bundesland anzukuendigen ist der haeufige Fall, und dort ist
		 * der Termin richtig. Ein vergangener Termin dagegen gehoert zur
		 * Vorlage, nicht zur Kopie — er wird nicht uebernommen, und weil er bei
		 * einer Veranstaltung Pflicht ist, faellt das beim Speichern auf.
		 */
		const terminVorlage = zuDatum(feed?.eventStart)
		const terminGilt = !kopie || (terminVorlage && terminVorlage > new Date())

		setTerminVerworfen(Boolean(kopie && terminVorlage && !terminGilt))
		setValue('eventStart', terminGilt ? terminVorlage : null)
		setValue('eventEnd', terminGilt ? zuDatum(feed?.eventEnd) : null)

		if (!feed) return

		/*
		 * Bearbeiten und Kopie uebernehmen den Stand der Nachricht bzw. der
		 * Vorlage. Nur das blanke Anlegen laesst das Feld leer — dort ist die
		 * Entscheidung Entwurf/aktiv Pflicht (Anforderung #164).
		 */
		setValue('publishStatus', feed.isDraft ? 'DRAFT' : 'ACTIVE')

		setValue('newsType', feed.newsType || '')
		setValue('title', feed.title || '')
		setValue('description', feed.description || '')
		setValue('place', feed.place || '')
		setValue('street', feed.street || '')
		setValue('zip', feed.zip || '')
		setValue('city', feed.city || '')
		setValue('country', feed.country || '')
		setValue('externalLink', feed.externalLink || '')
		setValue('states', feed.states || ['ALL'])
		setValue('roles', feed.roles)
		setValue('schoolType', feed.schoolType)
		setValue('companyTypes', feed.companyTypes)
		setValue('companyBranch', feed.companyBranch)
	}

	// Ein abgebrochenes Bearbeiten darf aus dem naechsten "Nachricht erstellen"
	// kein Update der zuletzt geoeffneten Nachricht machen.
	const handleClose = () => {
		reset()
		setId(null)
		setBildName(null)
		setBildDatei(null)
		setBildEntfernen(false)
		setIstKopie(false)
		setTerminVerworfen(false)
		setTab(ERSTES_REGISTER)
		setIsOpen(false)
		setRecipients(null)
		setHistory(null)
		setHistoryFailed(false)
	}

	// Den Verlauf gibt es erst, wenn es die Nachricht gibt.
	const sichtbareTabs = TABS.filter(item => !item.onlyExisting || id)
	const schritte = sichtbareTabs.filter(item => !item.noStep)
	const schrittNummer = key => schritte.findIndex(item => item.key === key) + 1

	// "Weiter" fuehrt durch die Schritte, nicht in den Verlauf — der ist ein
	// Nachschlagewerk und kein Teil des Wegs zum Speichern.
	const tabIndex = schritte.findIndex(item => item.key === tab)
	const isLastTab = tabIndex === schritte.length - 1

	const hatFehler = Object.keys(errors).length > 0

	// Aus der Zusammenfassung zurueck zu dem Register, in dem das Feld steht.
	const zumFeld = feld => () => setTab(registerFuerFeld(feld))

	/*
	 * Beim Bearbeiten ist "Speichern" auf jedem Schritt erreichbar: Wer nur den
	 * Endtermin verschiebt, hat danach nichts mehr zu pruefen. Beim Anlegen
	 * bleibt der Weg ueber die Schritte — dort ist die Zusammenfassung das erste
	 * Mal, dass die Nachricht als Ganzes zu sehen ist.
	 */
	const speichernImmerSichtbar = !!id && tab !== TAB_HISTORY

	return (
		<BaseModal
			onClose={handleClose}
			onOpen={handleOpen}
			nameEvent={types.openAddFeedModal}
			// Ein gewaehltes oder abgeraeumtes Titelbild ist eine Aenderung wie
			// jede andere — es steht nur nicht im Formular.
			isDirty={Boolean(Object.keys(dirtyFields).length || bildDatei || bildEntfernen)}
			/*
			 * Die Kopie sagt es in der Kopfzeile: Sonst steht dort "Nachricht
			 * erstellen" ueber einem Formular, das schon ausgefuellt ist.
			 */
			title={id ? t('Edit message') : t(istKopie ? 'Duplicate message' : 'Create message')}
			isLoading={isLoading}
			/*
			 * Breit genug, dass die sechs Register in einer Zeile stehen: Umgebrochen
			 * sahen sie aus wie zwei Gruppen und die Nummerierung verlor ihren Faden.
			 */
			maxWidth={1040}
			fitViewport
		>
			{({ closeModal }) => (
				<form className={styles.root} onSubmit={handleSubmit(form => onSubmit(form, closeModal), onInvalid)}>
					<div className={styles.tabs} ref={tabsRef}>
						{sichtbareTabs.map(item => {
							const hasError = item.fields.some(field => errors[field])

							return (
								<button
									type="button"
									key={item.key}
									className={clsx(
										styles.tab,
										item.key === tab && styles.tabActive,
										item.noStep && styles.tabAside
									)}
									onClick={() => setTab(item.key)}
								>
									{!item.noStep && <span className={styles.tabIndex}>{schrittNummer(item.key)}</span>}
									{t(item.label)}
									{hasError && <span className={styles.tabError} />}
								</button>
							)
						})}
					</div>

					<div className={clsx(styles.pane, tab !== TAB_SCHEDULE && styles.paneHidden)}>
						{/*
						 * Zwei verschiedene Fragen, deshalb zwei Gruppen: Wie lange
						 * die Nachricht zu sehen ist, und wann die Veranstaltung
						 * stattfindet. Bis 08.08.2026 war das ein Feldpaar fuer
						 * beides — an der Karte stand dann die Anzeigedauer als
						 * Termin, und eine Messe, die einen Tag dauert und ein
						 * halbes Jahr angekuendigt wird, war nicht zu erfassen.
						 */}
						<h4 className={styles.paneSubTitle}>{t('Display period')}</h4>
						<p className={styles.paneHint}>
							{t(
								'Decides when the message appears for students and teachers. The status follows from it.'
							)}
						</p>

						<div className={styles.row}>
							<DateTimeField
								required
								showTimeSelect
								dateFormat={DATE_TIME_FORMAT}
								className={styles.textField}
								control={control}
								error={errors.startDate}
								mb={25}
								label={t('Shown from')}
								name="startDate"
								placeholder={DATE_TIME_FORMAT}
							/>
							<DateTimeField
								required
								showTimeSelect
								dateFormat={DATE_TIME_FORMAT}
								className={styles.textField}
								control={control}
								error={errors.endDate}
								mb={25}
								label={t('Shown until')}
								name="endDate"
								placeholder={DATE_TIME_FORMAT}
							/>
						</div>

						{/* Nur beim Anlegen — bei einer bestehenden Nachricht stehen
						    dort ihre eigenen Werte, nicht die Vorbelegung. Bei einer
						    Kopie erklaert der Satz, warum hier nicht der Zeitraum der
						    Vorlage steht. */}
						{!id && (
							<p className={styles.uriTip}>
								{t(
									istKopie
										? 'The copy starts with today and one month ahead — the period of the original is not carried over.'
										: 'Prefilled with today and one month ahead — adjust as needed.'
								)}
							</p>
						)}

						{/*
						 * Nur bei einer Veranstaltung. Eine Neuigkeit hat keinen
						 * Termin — dort waeren zwei leere Pflichtfelder eine Frage,
						 * auf die es keine Antwort gibt.
						 */}
						{istVeranstaltung && (
							<div className={styles.eventBlock}>
								<h4 className={styles.paneSubTitle}>{t('Event date')}</h4>
								<p className={styles.paneHint}>
									{t('When it actually takes place — independent of how long it is announced.')}
								</p>

								{/*
								 * Ein leeres Pflichtfeld in einer sonst ausgefuellten
								 * Kopie sieht wie ein Fehler aus. Der Satz sagt, dass
								 * es Absicht war und warum.
								 */}
								{terminVerworfen && (
									<p className={styles.scheduleWarn}>
										{t(
											'The event date of the original is in the past and was not carried over — please enter the new date.'
										)}
									</p>
								)}

								<div className={styles.row}>
									<DateTimeField
										required
										showTimeSelect
										dateFormat={DATE_TIME_FORMAT}
										className={styles.textField}
										control={control}
										error={errors.eventStart}
										mb={25}
										label={t('Event start')}
										name="eventStart"
										placeholder={DATE_TIME_FORMAT}
									/>
									<DateTimeField
										showTimeSelect
										dateFormat={DATE_TIME_FORMAT}
										className={styles.textField}
										control={control}
										error={errors.eventEnd}
										mb={25}
										label={`${t('Event end')} (${t('optional')})`}
										name="eventEnd"
										placeholder={DATE_TIME_FORMAT}
									/>
								</div>

								{/*
								 * Kein Fehler, sondern ein Hinweis: Ein Termin nach dem
								 * Ende der Anzeige ist erlaubt (eine Ankuendigung darf
								 * vorher auslaufen), aber fast immer ein Versehen.
								 */}
								{terminAusserhalb && <p className={styles.scheduleWarn}>{terminAusserhalb}</p>}
							</div>
						)}
					</div>

					<div className={clsx(styles.pane, tab !== TAB_CONTENT && styles.paneHidden)}>
						<SelectField
							label={t('Message type')}
							className={styles.textField}
							valuesToOptions
							mb={25}
							control={control}
							name="newsType"
							placeholder={t('Select type or types')}
							options={newsTypesOptions}
							required
							error={errors.newsType}
						/>
						<TextField
							className={styles.textField}
							required
							mb={25}
							label={t('Title')}
							register={register('title')}
							error={errors.title}
							placeholder={t('Title')}
						/>
						<TextAreaField
							mb={25}
							label={t('description')}
							required
							height={180}
							maxLength={500}
							showLettersLeft
							value={values.description || ''}
							register={register('description')}
							error={errors.description}
							placeholder={t('description')}
						/>

						{/*
						 * Das Titelbild steht beim Inhalt, weil es einer ist: Es
						 * ist das Erste, was auf der Karte im Dashboard zu sehen
						 * ist — vor Titel und Text.
						 */}
						<h4 className={styles.paneSubTitle}>{t('Cover image')}</h4>
						<CoverUploader
							src={bildAnzeige}
							onSelect={datei => {
								setBildDatei(datei)
								setBildEntfernen(false)
							}}
							/*
							 * Entfernen heisst hier vormerken, nicht loeschen: Wer
							 * den Dialog abbricht, soll sein Bild behalten.
							 */
							onClean={() => {
								setBildDatei(null)
								setBildEntfernen(true)
							}}
						/>
					</div>

					<div className={clsx(styles.pane, tab !== TAB_ADDRESS && styles.paneHidden)}>
						<p className={styles.paneHint}>
							{t('Where does it take place? A street address, a link, or both — nothing is required.')}
						</p>

						<TextField
							className={styles.textField}
							mb={25}
							label={t('Street and number')}
							register={register('street')}
							error={errors.street}
							placeholder={t('Street and number')}
						/>

						<div className={styles.row}>
							<TextField
								className={styles.zipField}
								mb={25}
								label={t('Postal code')}
								register={register('zip')}
								error={errors.zip}
								placeholder={t('Postal code')}
							/>
							<TextField
								className={styles.textField}
								mb={25}
								label={t('City or town')}
								register={register('city')}
								error={errors.city}
								placeholder={t('City or town')}
							/>
						</div>

						<TextField
							className={styles.textField}
							mb={25}
							label={t('Country')}
							register={register('country')}
							error={errors.country}
							placeholder={t('Country')}
						/>

						{/*
						 * Die alte Freitext-Ortsangabe. Sie steht nur da, wo sie
						 * gefuellt ist — bei einer neuen Nachricht waere sie ein
						 * Feld, das niemand mehr braucht. Automatisch aufgeteilt
						 * wird sie nicht: Die Eintraege sind uneinheitlich, und
						 * geraten landet die Hausnummer in der PLZ.
						 */}
						{!!values.place && (
							<TextField
								className={styles.textField}
								mb={25}
								label={t('Location (previous entry)')}
								register={register('place')}
								error={errors.place}
								placeholder={t('Location (previous entry)')}
							/>
						)}

						<TextField
							className={styles.textField}
							label={t('External Link')}
							register={register('externalLink')}
							error={errors.externalLink}
							placeholder={t('External Link')}
						/>
						<span className={styles.uriTip}>
							{t('Example')}: https://google.com · {t('Also for Teams, Zoom or a registration page.')}
						</span>
					</div>

					<div className={clsx(styles.pane, tab !== TAB_AUDIENCE && styles.paneHidden)}>
						<p className={styles.paneHint}>{t('An empty field places no restriction.')}</p>

						{/*
						 * Zwei Spalten: Fuenf Auswahlfelder untereinander waren im Dialog
						 * eine halbe Bildschirmhoehe, und die Reichweite darunter — also
						 * das, was die Einstellung bewirkt — rutschte aus dem Blick.
						 * Die Branche steht ueber beide Spalten, ihre Namen sind lang.
						 */}
						<div className={styles.audienceGrid}>
							<SelectField
								menuPortal
								label={t('state')}
								className={styles.textField}
								isMulti
								mb={25}
								control={control}
								error={errors.states}
								valuesToOptions
								options={[
									{
										value: 'ALL',
										label: t('All states')
									},
									...StateOptions.map(v => ({
										label: v.label,
										value: v.value
									})).sort((a, b) => a.label.localeCompare(b.label))
								]}
								name="states"
								placeholder={t('Select state or states')}
								required={false}
							/>
							<SelectField
								menuPortal
								label={t('User roles')}
								className={styles.textField}
								control={control}
								error={errors.roles}
								valuesToOptions
								mb={25}
								isMulti
								options={rolesOptions}
								name="roles"
								placeholder={t('Select role or roles')}
								required={false}
							/>
							<SelectField
								menuPortal
								label={t('School Types')}
								className={styles.textField}
								mb={25}
								valuesToOptions
								isMulti
								control={control}
								name="schoolType"
								error={errors.schoolType}
								placeholder={t('Select')}
								options={props.schoolTypes?.map(v => ({
									value: v.value,
									label: v.value
								}))}
								required={false}
							/>
							<SelectField
								menuPortal
								label={t('Company type')}
								className={styles.textField}
								error={errors.companyTypes}
								valuesToOptions
								isMulti
								mb={25}
								control={control}
								options={companyTypesOptions}
								name="companyTypes"
								placeholder={t('Select type or types')}
								required={false}
							/>
							<SelectField
								menuPortal
								label={t('branch')}
								className={clsx(styles.textField, styles.audienceWide)}
								valuesToOptions
								isMulti
								mb={25}
								control={control}
								name="companyBranch"
								placeholder={t('branch')}
								options={props.branch?.map(v => ({
									value: v.value_EN,
									label: i18n.language === TLanguages.EN ? v.value_EN : v.value_DE
								}))}
								required={false}
							/>
						</div>

						<div className={styles.audiencePreview}>
							<div className={styles.reach}>
								<span className={clsx(styles.reachCount, reachFailed && styles.reachCountFailed)}>
									{reachFailed ? '—' : recipients === null ? '…' : recipients.total}
								</span>
								<span className={styles.reachLabel}>
									{reachFailed
										? t('Reach cannot be determined right now')
										: t('people receive this message')}
									{recipients !== null && recipients.total > 0 && (
										<span className={styles.reachSplit}>
											{recipients.students} {t('STUDENT')} · {recipients.teachers} {t('TEACHER')}
										</span>
									)}
								</span>
							</div>
							<p
								className={styles.reachScope}
								title={
									restrictions.length
										? restrictions.map(item => `${item.label}: ${item.value}`).join(' · ')
										: t('No restriction — all users')
								}
							>
								{restrictions.length
									? restrictions.map(item => `${item.label}: ${item.value}`).join(' · ')
									: t('No restriction — all users')}
							</p>
						</div>
					</div>

					<div className={clsx(styles.pane, tab !== TAB_REVIEW && styles.paneHidden)}>
						{hatFehler && (
							<p className={styles.reviewError}>
								{t('Some entries are incomplete — the affected steps are marked with a dot.')}
							</p>
						)}

						{/*
						 * Entwurf oder aktiv (Anforderung #164). Die Entscheidung
						 * steht hier, wo alles zusammenlaeuft: Sie ist der letzte
						 * Schritt vor dem Speichern. Beim Anlegen ist sie nicht
						 * vorbelegt — wer speichert, hat sich entschieden. Ein
						 * Entwurf laesst sich spaeter aktivieren und eine aktive
						 * Nachricht wieder auf Entwurf zuruecksetzen — hier oder
						 * per Menue an der Karte.
						 */}
						<SelectField
							label={t('Status')}
							className={styles.textField}
							valuesToOptions
							mb={10}
							control={control}
							name="publishStatus"
							placeholder={t('Select')}
							options={[
								{ value: 'ACTIVE', label: t('Active — visible during the display period') },
								{ value: 'DRAFT', label: t('Draft — visible to the administration only') }
							]}
							required
							error={errors.publishStatus}
						/>
						<p className={styles.uriTip}>
							{t(
								'Draft or active? A draft stays hidden from students and teachers until it is activated.'
							)}
						</p>

						<div className={styles.summary}>
							<SummaryRow label={t('Message type')} onEdit={zumFeld('newsType')} editLabel={t('Change')}>
								{values.newsType ? t(values.newsType) : <em>{t('Not set')}</em>}
							</SummaryRow>
							<SummaryRow label={t('Title')} onEdit={zumFeld('title')} editLabel={t('Change')}>
								{values.title || <em>{t('Not set')}</em>}
							</SummaryRow>
							<SummaryRow label={t('Cover image')} onEdit={zumFeld('title')} editLabel={t('Change')}>
								{bildAnzeige ? (
									<img className={styles.summaryCover} src={bildAnzeige} alt="" />
								) : (
									<em>{t('No cover image')}</em>
								)}
							</SummaryRow>
							<SummaryRow
								label={t('Display period')}
								onEdit={zumFeld('startDate')}
								editLabel={t('Change')}
							>
								{fmtDateTime(values.startDate) && fmtDateTime(values.endDate) ? (
									`${fmtDateTime(values.startDate)} – ${fmtDateTime(values.endDate)}`
								) : (
									<em>{t('Not set')}</em>
								)}
							</SummaryRow>
							{istVeranstaltung && (
								<SummaryRow
									label={t('Event date')}
									onEdit={zumFeld('eventStart')}
									editLabel={t('Change')}
								>
									{fmtDateTime(values.eventStart) ? (
										[fmtDateTime(values.eventStart), fmtDateTime(values.eventEnd)]
											.filter(Boolean)
											.join(' – ')
									) : (
										<em>{t('Not set')}</em>
									)}
								</SummaryRow>
							)}
							{!!anschrift && (
								<SummaryRow label={t('Address')} onEdit={zumFeld('street')} editLabel={t('Change')}>
									{anschrift}
								</SummaryRow>
							)}
							{!!values.place && (
								<SummaryRow
									label={t('Location (previous entry)')}
									onEdit={zumFeld('place')}
									editLabel={t('Change')}
								>
									{values.place}
								</SummaryRow>
							)}
							{!!values.externalLink && (
								<SummaryRow
									label={t('External Link')}
									onEdit={zumFeld('externalLink')}
									editLabel={t('Change')}
								>
									{values.externalLink}
								</SummaryRow>
							)}
							<SummaryRow label={t('Reach')}>
								{reachFailed ? (
									<em>{t('Reach cannot be determined right now')}</em>
								) : recipients === null ? (
									<em>{t('Not set')}</em>
								) : (
									<strong>
										{recipients.total} {t('people receive this message')}
									</strong>
								)}
							</SummaryRow>
							<SummaryRow label={t('Target groups')} onEdit={zumFeld('roles')} editLabel={t('Change')}>
								{restrictions.length ? (
									<span className={styles.summaryChips}>
										{restrictions.map(item => (
											<span key={item.key} className={styles.summaryChip}>
												{item.label}: {item.value}
											</span>
										))}
									</span>
								) : (
									t('all users')
								)}
							</SummaryRow>
							<SummaryRow
								label={t('description')}
								onEdit={zumFeld('description')}
								editLabel={t('Change')}
							>
								{values.description || <em>{t('Not set')}</em>}
							</SummaryRow>
						</div>

						{/*
						 * Hier stand ein Schalter "zusaetzlich Email an alle Nutzer
						 * senden". Der Server hat ihn nie ausgewertet: Das Feld ist
						 * im Schema Pflicht, wird uebertragen und dann verworfen —
						 * das Modell hat keine solche Spalte, und der Controller
						 * legt ausschliesslich eine interne Benachrichtigung an.
						 * Es ist also nie eine E-Mail hinausgegangen, obwohl der
						 * Haken das versprochen hat.
						 *
						 * Bis der Versand tatsaechlich gebaut ist, steht hier, was
						 * wirklich passiert. Ein Schalter ohne Wirkung ist
						 * schlimmer als keiner: Er laesst die Administration
						 * glauben, sie haette alle erreicht.
						 */}
						<p className={styles.deliveryNote}>{t('How this message is delivered')}</p>
					</div>

					<div className={clsx(styles.pane, tab !== TAB_HISTORY && styles.paneHidden)}>
						{/*
						 * Erst die Kommentare, dann der Aenderungsverlauf: beides
						 * in einem Reiter, damit es zu jedem Datensatz dieselbe
						 * eine Stelle gibt. Der Aenderungsverlauf bleibt, wie er
						 * war — er zeigt Vorher/Nachher je Feld und damit mehr,
						 * als ein Kommentareintrag hergeben koennte.
						 */}
						<RecordComments entityType={RECORD_TYPES.NEWS} entityId={id} />

						<h4 className={styles.paneSubTitle}>{t('Change history')}</h4>
						<p className={styles.paneHint}>
							{t('The log starts with its introduction — what happened before is not recorded.')}
						</p>

						{historyFailed ? (
							<p className={styles.historyEmpty}>{t('The history cannot be loaded right now.')}</p>
						) : history === null ? (
							<p className={styles.historyEmpty}>…</p>
						) : history.length === 0 ? (
							<p className={styles.historyEmpty}>{t('No changes recorded yet.')}</p>
						) : (
							<ul className={styles.history}>
								{history.map(eintrag => (
									<li key={eintrag.id} className={styles.historyItem}>
										<p className={styles.historyHead}>
											<span className={styles.historyTime}>{fmtDateTime(eintrag.createdAt)}</span>
											<span className={styles.historyWho}>
												{eintrag.editorName || t('Unknown')}
											</span>
											<span className={styles.historyAction}>
												{eintrag.action === 'CREATED' ? t('Created') : t('Edited')}
											</span>
										</p>

										{!!eintrag.changes?.length && (
											<ul className={styles.historyChanges}>
												{eintrag.changes.map((change, index) => (
													<li key={`${change.field}-${index}`}>
														<span className={styles.historyField}>
															{feldLabel(change.field)}
														</span>
														{'to' in change ? (
															<>
																<span className={styles.historyFrom}>
																	{feldWert(change.field, change.from)}
																</span>
																<span className={styles.historyArrow}>→</span>
																<span className={styles.historyTo}>
																	{feldWert(change.field, change.to)}
																</span>
															</>
														) : (
															<span className={styles.historyFrom}>{t('changed')}</span>
														)}
													</li>
												))}
											</ul>
										)}
									</li>
								))}
							</ul>
						)}
					</div>

					<div className={styles.footer}>
						{/*
						 * Im Verlauf ist man nicht auf dem Weg zum Speichern —
						 * "Weiter" wuerde von dort an den Anfang springen.
						 * Deshalb nur der Rueckweg zum letzten Schritt.
						 */}
						{tab === TAB_HISTORY ? (
							<Btn theme="secondary" onClick={() => setTab(TAB_REVIEW)}>
								{t('Back')}
							</Btn>
						) : (
							<>
								<Btn
									theme="secondary"
									disabled={tabIndex <= 0}
									onClick={() => setTab(schritte[tabIndex - 1].key)}
								>
									{t('Back')}
								</Btn>

								<div className={styles.footerRight}>
									{isLastTab ? (
										<Btn
											loading={isLoading}
											type="submit"
											icon="send"
											iconSize={{ w: 15, h: 15 }}
											iconColor="#fff"
										>
											{t('save')}
										</Btn>
									) : (
										<>
											{speichernImmerSichtbar && (
												<Btn theme="secondary" loading={isLoading} type="submit">
													{t('save')}
												</Btn>
											)}
											<Btn onClick={() => setTab(schritte[tabIndex + 1].key)}>
												{t('Next step')}
											</Btn>
										</>
									)}
								</div>
							</>
						)}
					</div>
				</form>
			)}
		</BaseModal>
	)
}

AddFeedModal.propTypes = {
	schoolTypes: PropTypes.array,
	branch: PropTypes.array,
	role: PropTypes.string,
	state: PropTypes.string
}

export default injectRole(AddFeedModal, state => ({
	state: state.user.profile.state
}))
